import React from 'react';

const GeneralInformation = ({
  asset,
  isEditing,
  onUpdate
}) => {

  // =====================================
  // HANDLE CHANGE
  // =====================================

  const handleChange = (field, value) => {
    onUpdate({
      ...asset,
      [field]: value
    });
  };

  // =====================================
  // NORMALIZE DATE FOR INPUT TYPE="DATE"
  // =====================================

  const formatDateForInput = (value) => {
    if (!value) {
      return '';
    }

    // Already in YYYY-MM-DD format
    if (
      typeof value === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
      return value;
    }

    // Laravel/MySQL format: YYYY-MM-DD HH:mm:ss
    if (
      typeof value === 'string' &&
      /^\d{4}-\d{2}-\d{2}\s/.test(value)
    ) {
      return value.substring(0, 10);
    }

    // ISO format: 2026-10-07T00:00:00.000000Z
    if (
      typeof value === 'string' &&
      /^\d{4}-\d{2}-\d{2}T/.test(value)
    ) {
      return value.substring(0, 10);
    }

    // DD/MM/YYYY
    if (
      typeof value === 'string' &&
      /^\d{2}\/\d{2}\/\d{4}$/.test(value)
    ) {
      const [day, month, year] = value.split('/');
      return `${year}-${month}-${day}`;
    }

    // Date object / other valid date formats
    const date = new Date(value);

    if (!Number.isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return '';
  };

  // =====================================
  // GET DATES
  // =====================================

  const purchaseDate =
    asset?.purchase_date ||
    asset?.purchaseDate ||
    asset?.purchaseDateValue ||
    '';

  const warrantyDate =
    asset?.warranty_expiration ||
    asset?.warrantyExpiration ||
    asset?.warranty_expiry ||
    asset?.warrantyExpiry ||
    '';

  const purchaseDateValue = formatDateForInput(purchaseDate);
  const warrantyDateValue = formatDateForInput(warrantyDate);

  // =====================================
  // DISPLAY DATE
  // =====================================

  const formatDateForDisplay = (value) => {
    const normalized = formatDateForInput(value);
    if (!normalized) return '—';

    const [year, month, day] = normalized.split('-');
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const inputClass = (editable = isEditing) =>
    `w-full border rounded-lg p-3 text-sm transition-colors ${
      !editable
        ? 'border-border bg-muted/50 text-muted-foreground cursor-not-allowed'
        : 'border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary'
    }`;

  // =====================================
  // RENDER
  // =====================================

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">
          General Information
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Core identity, location, and operational parameters of this asset
        </p>
      </div>

      {/* FORM GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ASSET ID */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Asset ID
          </label>
          <input
            type="text"
            value={asset?.asset_id || ''}
            disabled
            className={inputClass(false)}
          />
        </div>

        {/* BARCODE */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Barcode
          </label>
          <input
            type="text"
            value={asset?.barcode || ''}
            disabled
            className={inputClass(false)}
          />
        </div>

        {/* BRAND */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Brand
          </label>
          <input
            type="text"
            value={asset?.brand || ''}
            disabled={!isEditing}
            onChange={(e) => handleChange('brand', e.target.value)}
            className={inputClass()}
            placeholder="e.g. Apple, Dell, Lenovo"
          />
        </div>

        {/* MODEL */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Model
          </label>
          <input
            type="text"
            value={asset?.model || ''}
            disabled={!isEditing}
            onChange={(e) => handleChange('model', e.target.value)}
            className={inputClass()}
            placeholder="e.g. MacBook Pro 16, XPS 15"
          />
        </div>

        {/* SERIAL NUMBER */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Serial Number
          </label>
          <input
            type="text"
            value={asset?.serial_number || ''}
            disabled={!isEditing}
            onChange={(e) => handleChange('serial_number', e.target.value)}
            className={inputClass()}
            placeholder="e.g. C02G40ALMD6R"
          />
        </div>

        {/* DEPARTMENT */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Department
          </label>
          <input
            type="text"
            value={asset?.department || ''}
            disabled={!isEditing}
            onChange={(e) => handleChange('department', e.target.value)}
            className={inputClass()}
            placeholder="e.g. Engineering, Finance"
          />
        </div>

        {/* LOCATION */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Location
          </label>
          <input
            type="text"
            value={asset?.location || ''}
            disabled={!isEditing}
            onChange={(e) => handleChange('location', e.target.value)}
            className={inputClass()}
            placeholder="e.g. Building A - Floor 2"
          />
        </div>

        {/* STATUS */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Status
          </label>
          <select
            value={asset?.status || 'active'}
            disabled={!isEditing}
            onChange={(e) => handleChange('status', e.target.value)}
            className={inputClass()}
          >
            <option value="active">Active</option>
            <option value="spare">Spare</option>
            <option value="repair">Repair</option>
            <option value="retired">Retired</option>
          </select>
        </div>

        {/* PURCHASE DATE */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Purchase Date
          </label>
          {isEditing ? (
            <input
              type="date"
              value={purchaseDateValue}
              onChange={(e) => handleChange('purchase_date', e.target.value)}
              className={inputClass(true)}
            />
          ) : (
            <div className={inputClass(false)}>
              {formatDateForDisplay(purchaseDate)}
            </div>
          )}
        </div>

        {/* WARRANTY EXPIRATION */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Warranty Expiration
          </label>
          {isEditing ? (
            <input
              type="date"
              value={warrantyDateValue}
              onChange={(e) => handleChange('warranty_expiration', e.target.value)}
              className={inputClass(true)}
            />
          ) : (
            <div className={inputClass(false)}>
              {formatDateForDisplay(warrantyDate)}
            </div>
          )}
        </div>

        {/* PRICE */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Price / Acquisition Value
          </label>
          <input
            type="number"
            step="0.01"
            value={asset?.price ?? asset?.purchase_price ?? ''}
            disabled={!isEditing}
            onChange={(e) => {
              onUpdate({
                ...asset,
                price: e.target.value,
                purchase_price: e.target.value
              });
            }}
            className={inputClass()}
            placeholder="0.00"
          />
        </div>

        {/* OWNER */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-foreground">
            Current Owner / Custodian
          </label>
          <input
            type="text"
            value={asset?.owner?.name || 'Unassigned'}
            disabled
            className={inputClass(false)}
          />
        </div>
      </div>

      {/* DESCRIPTION */}
      <div className="mt-6">
        <label className="block text-sm font-medium mb-1.5 text-foreground">
          Description
        </label>
        <textarea
          rows={4}
          value={asset?.description || ''}
          disabled={!isEditing}
          onChange={(e) => handleChange('description', e.target.value)}
          className={inputClass()}
          placeholder="Enter asset description or purpose..."
        />
      </div>

      {/* QR CODE DATA */}
      <div className="mt-6">
        <label className="block text-sm font-medium mb-1.5 text-foreground">
          QR Code Payload Data
        </label>
        <textarea
          rows={3}
          value={asset?.qr_code || ''}
          disabled
          className={`${inputClass(false)} font-mono text-xs`}
        />
      </div>
    </div>
  );
};

export default GeneralInformation;
